/**
 * Engine creation (P0-T03): WebGPU when tier >= mid and available, else WebGL 2.
 * Engine settings per SPEC-01 §3.
 */
import type { Nullable } from 'babylonjs';
import type { Tier } from '@kota-nova/shared';
import { TIER_SETTINGS } from '@kota-nova/shared';

export interface EngineBundle {
  engine: import('babylonjs').AbstractEngine;
  babylon: typeof import('babylonjs');
  backend: 'webgpu' | 'webgl2';
}

export async function createEngine(canvas: HTMLCanvasElement, tier: Tier): Promise<EngineBundle> {
  const babylon = await import('babylonjs');
  let engine: import('babylonjs').AbstractEngine | null = null;
  let backend: EngineBundle['backend'] = 'webgl2';

  const params = TIER_SETTINGS[tier];
  if (tier !== 'low') {
    engine = await tryWebGPU(babylon, canvas, params.maxDpr);
    if (engine) backend = 'webgpu';
  }
  if (!engine) {
    const EngineCtor = babylon.Engine as unknown as new (
      c: HTMLCanvasElement,
      aa: boolean,
      o: Record<string, unknown>,
    ) => import('babylonjs').Engine;
    engine = new EngineCtor(canvas, backend === 'webgl2' && params.msaa > 1, {
      adaptToDeviceRatio: true,
      failIfMajorPerformanceCaveat: false,
      premultipliedAlpha: false,
      powerPreference: 'high-performance',
      staggeredStabilizedFrameTime: tier === 'high',
    });
  }

  engine.setHardwareScalingLevel(1 / renderScaleFor(tier));
  void params;
  return { engine, babylon, backend };
}

async function tryWebGPU(
  babylon: typeof import('babylonjs'),
  canvas: HTMLCanvasElement,
  maxDpr: number,
): Promise<Nullable<import('babylonjs').AbstractEngine>> {
  void maxDpr;
  try {
    const nav = navigator as Navigator & { gpu?: { requestAdapter(): Promise<unknown> } };
    if (!nav.gpu) return null;
    const adapter = await nav.gpu.requestAdapter();
    if (!adapter) return null;
    interface WebGPUInit {
      init(): Promise<void>;
    }
    const WebGPUEngineCtor = (
      babylon as unknown as {
        WebGPUEngine: new (
          c: HTMLCanvasElement,
          o: Record<string, unknown>,
        ) => import('babylonjs').AbstractEngine & WebGPUInit;
      }
    ).WebGPUEngine;
    if (!WebGPUEngineCtor) return null;
    const eng = new WebGPUEngineCtor(canvas, {
      adaptToDeviceRatio: true,
      antialias: false,
      // P1-T06: city material plugins inject GLSL even on WebGPU
      // (PBRMaterial._forceGLSL). Babylon converts GLSL->SPIR-V->WGSL with
      // glslang + twgsl; vendored under public/decoders (no CDN dependency).
      glslangOptions: { jsPath: '/decoders/glslang/glslang.js', wasmPath: '/decoders/glslang/glslang.wasm' },
      twgslOptions: { jsPath: '/decoders/twgsl/twgsl.js', wasmPath: '/decoders/twgsl/twgsl.wasm' },
    });
    await eng.init();
    return eng;
  } catch {
    return null; // fall back to WebGL2
  }
}

/** Render scale: start at 1.0 hardware pixels; adaptive quality lowers below. */
export function renderScaleFor(_tier: Tier): number {
  return 1;
}
